'use client'

import { CircularProgress } from '@mui/material'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import Hls from 'hls.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isIOS, isMobile, isSafari } from 'react-device-detect'
import { Controller } from './Controller'
import { FlashIcon } from './FlashIcon'
import { Subtitle } from './Subtitle'
import { FullscreenContext } from './useFullscreen'
import { LangContext } from './useLang'
import { DefaultLogger, LoggerContext, LogType } from './useLogger'
import { VideoScaleProvider } from './useVideoScale'

type Props = {
  title: string
  src: string | undefined
  duration: number
  vtts: {
    default: boolean
    url: string
    locale: string
    label: string
  }[]
  hlsParameter: string
  loggers: {
    [key in LogType]?: () => void
  }
  viewingTime?: {
    minutes: number
    seconds: number
    update: (currentTime: number) => void
  }
  onPlay?: () => void
  onEnd?: () => void
}

export const PlayerBase = ({
  title,
  src,
  duration,
  vtts,
  hlsParameter,
  loggers,
  viewingTime,
  onPlay,
  onEnd,
}: Props) => {
  const normalizedLoggers = useMemo(() => ({ ...DefaultLogger, ...loggers }), [loggers])
  const baseRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLVideoElement>(null)
  const [hls, setHls] = useState<Hls | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0.0)
  const [fullscreen, setFullscreen] = useState(false)
  const [lang, setLang] = useState<string | null>(null)
  const [hover, setHover] = useState(false)

  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)

  const showedControls = useMemo(() => {
    if (!isPlaying) return true

    return hover && !isIdle
  }, [isIdle, isPlaying, hover])

  useEffect(() => {
    const handleMouseMove = () => {
      setIsIdle(false)

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)

      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true)
      }, 3000)
    }

    handleMouseMove()

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const videoElem = ref.current
    if (videoElem == null || !src) return

    if (Hls.isSupported() && !isSafari && !isMobile) {
      const hls = new Hls()
      hls.config.xhrSetup = (xhr, url) => {
        xhr.open('GET', `${url}?${hlsParameter}`, true)
      }
      hls.detachMedia()
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })
      hls.attachMedia(videoElem)
      setHls(hls)
    }
  }, [src, hlsParameter])

  const logged = useRef({
    watch1Sec: false,
    watch3Sec: false,
    watch5Sec: false,
    watch10Sec: false,
    watch15Sec: false,
    watch30Sec: false,
    watchComplete: false,
  })

  const handleLoaded = useCallback(() => {
    const video = ref.current
    if (!video) return

    if (viewingTime) {
      video.currentTime = viewingTime.minutes * 60 + viewingTime.seconds
    } else {
      video.currentTime = 0
    }

    video.play()

    video.addEventListener('timeupdate', () => {
      if (video.currentTime >= 1 && !logged.current.watch1Sec) {
        normalizedLoggers.watch1Sec()
        logged.current.watch1Sec = true
      }
      if (video.currentTime >= 3 && !logged.current.watch3Sec) {
        normalizedLoggers.watch3Sec()
        logged.current.watch3Sec = true
      }
      if (video.currentTime >= 5 && !logged.current.watch5Sec) {
        normalizedLoggers.watch5Sec()
        logged.current.watch5Sec = true
      }
      if (video.currentTime >= 10 && !logged.current.watch10Sec) {
        normalizedLoggers.watch10Sec()
        logged.current.watch10Sec = true
      }
      if (video.currentTime >= 15 && !logged.current.watch15Sec) {
        normalizedLoggers.watch15Sec()
        logged.current.watch15Sec = true
      }
      if (video.currentTime >= 30 && !logged.current.watch30Sec) {
        normalizedLoggers.watch30Sec()
        logged.current.watch30Sec = true
      }
      if (video.currentTime >= video.duration * 0.9 && !logged.current.watchComplete) {
        normalizedLoggers.watchComplete()
        logged.current.watchComplete = true
      }

      if (viewingTime) {
        viewingTime.update(video.currentTime)
      }
    })

    const handleEndFullscreen = () => {
      setFullscreen(false)
      normalizedLoggers.exitFullscreen()
    }
    video.addEventListener('webkitendfullscreen', handleEndFullscreen)

    let animationFrameId: number

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime)
      animationFrameId = requestAnimationFrame(handleTimeUpdate)
    }

    animationFrameId = requestAnimationFrame(handleTimeUpdate)
    return () => {
      cancelAnimationFrame(animationFrameId)
      video.removeEventListener('webkitendfullscreen', handleEndFullscreen)
    }
  }, [normalizedLoggers, viewingTime])

  const handleChangeFullscreen = useCallback(
    (value: boolean) => {
      if (value) {
        if (isIOS) {
          const video = ref.current as HTMLVideoElement & { webkitEnterFullscreen?: () => void }
          video.webkitEnterFullscreen?.()
          video.play()
        } else {
          baseRef?.current
            ?.requestFullscreen()
            .then(() => {
              ref.current?.play()
            })
            .catch((err) => {
              console.error('Error attempting to enable full-screen mode:', err)
            })
        }
        setFullscreen(true)
        normalizedLoggers.fullscreen()
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        }
        setFullscreen(false)
        normalizedLoggers.exitFullscreen()
      }
    },
    [baseRef, ref, normalizedLoggers],
  )

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement
      setFullscreen(isNowFullscreen)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const [flashIconStr, setFlashIconStr] = useState<string>('')
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const flashIcon = useCallback((name: string) => {
    setFlashIconStr(name)
    setVisible(false)

    requestAnimationFrame(() => {
      setVisible(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setVisible(false), 500)
    })
  }, [])

  return (
    <LoggerContext.Provider value={normalizedLoggers}>
      <FullscreenContext.Provider value={{ fullscreen, setFullscreen: handleChangeFullscreen }}>
        <LangContext.Provider value={{ lang, setLang }}>
          <AspectRatio
            ratio={fullscreen ? undefined : 16 / 9}
            ref={baseRef}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#000',
              }}
            >
              {src ? (
                <video
                  ref={ref}
                  style={{ width: '100%', height: '100%' }}
                  autoPlay
                  playsInline
                  onPlay={() => {
                    setIsPlaying(true)
                    onPlay?.()
                  }}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => {
                    setIsPlaying(false)
                    onEnd?.()
                  }}
                  onLoadedMetadata={handleLoaded}
                >
                  <source src={src} type='application/vnd.apple.mpegurl' />
                  {vtts.map((vtt) => (
                    <track
                      key={vtt.locale}
                      src={`/proxy_resource${new URL(vtt.url).pathname}`}
                      kind='captions'
                      srcLang={vtt.locale}
                      label={vtt.label}
                    />
                  ))}
                </video>
              ) : (
                <CircularProgress color='primary' />
              )}
            </div>
            {ref.current && baseRef.current && (
              <VideoScaleProvider video={ref.current}>
                <Subtitle video={ref.current} showedControls={showedControls} />
                <Controller
                  title={title}
                  video={ref.current}
                  hls={hls}
                  showedControls={showedControls}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  vtts={vtts}
                  flashIcon={flashIcon}
                />
                <FlashIcon icon={flashIconStr} visible={visible} />
              </VideoScaleProvider>
            )}
          </AspectRatio>
        </LangContext.Provider>
      </FullscreenContext.Provider>
    </LoggerContext.Provider>
  )
}
